import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageSquare, ThumbsUp, ThumbsDown, CheckCircle, 
  Calendar, User, MoreVertical, Edit, Trash2, Flag, Share2 
} from 'lucide-react';
import toast from 'react-hot-toast';
import MarkdownRenderer from '../MarkdownRenderer';

const QuestionItem = ({ question, courseId, onVote, onDelete, user }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  
  const handleVote = (voteType) => {
    onVote(question.id, voteType);
  };
  
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      onDelete(question.id);
    }
  };
  
  const handleShare = () => {
    const url = `${window.location.origin}/courses/${courseId}/questions/${question.id}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
        .then(() => toast.success('Link copied to clipboard'))
        .catch(() => toast.error('Failed to copy link'));
    } else {
      // Fallback for browsers that don't support clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard');
      } catch (err) {
        toast.error('Failed to copy link');
      } finally {
        document.body.removeChild(textarea);
      }
    }
    
    setShowDropdown(false);
  };
  
  const handleReport = () => {
    toast.info('Report functionality coming soon');
    setShowDropdown(false);
  };
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const isOwner = user && question.userId === user.id;
  const isModOrAdmin = user && (user.role === 'moderator' || user.role === 'admin');
  
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-red-300 dark:hover:border-red-700 transition-colors bg-white dark:bg-gray-800">
      <div className="flex flex-col md:flex-row">
        {/* Vote column */}
        <div className="flex md:flex-col items-center md:items-center space-x-4 md:space-x-0 mr-4">
          <button
            onClick={() => handleVote('upvote')}
            className={`p-1 rounded-full ${
              question.userVote === 'upvote'
                ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400'
            }`}
          >
            <ThumbsUp size={20} />
          </button>
          
          <span className="text-lg font-medium text-gray-700 dark:text-gray-300 md:my-2">
            {question.upvotes - question.downvotes}
          </span>
          
          <button
            onClick={() => handleVote('downvote')}
            className={`p-1 rounded-full ${
              question.userVote === 'downvote'
                ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400'
            }`}
          >
            <ThumbsDown size={20} />
          </button>
        </div>
        
        {/* Question content */}
        <div className="flex-1">
          <div className="flex justify-between">
            <Link
              to={`/courses/${courseId}/questions/${question.id}`}
              className="text-lg font-medium text-gray-900 dark:text-white hover:text-red-600 dark:hover:text-red-400"
            >
              {question.title}
            </Link>
            
            {/* Status badges */}
            <div className="flex items-center">
              {question.semester && question.year && (
                <span className="flex items-center px-2 py-1 mr-2 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                  {question.semester} {question.year}
                  {question.professorName && ` • ${question.professorName}`}
                </span>
              )}
              
              {question.isSolved && (
                <span className="flex items-center px-2 py-1 mr-2 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                  <CheckCircle size={14} className="mr-1" />
                  Solved
                </span>
              )}
              
              {/* Dropdown menu */}
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full focus:outline-none"
                >
                  <MoreVertical size={16} />
                </button>
                
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-[9999] border border-gray-200 dark:border-gray-700">
                    <ul className="py-1">
                      <li>
                        <button
                          onClick={handleShare}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <Share2 size={16} className="mr-2" />
                          Share
                        </button>
                      </li>
                      
                      {(isOwner || isModOrAdmin) && (
                        <>
                          <li>
                            <Link
                              to={`/courses/${courseId}/questions/${question.id}/edit`}
                              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Edit size={16} className="mr-2" />
                              Edit
                            </Link>
                          </li>
                          <li>
                            <button
                              onClick={handleDelete}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <Trash2 size={16} className="mr-2" />
                              Delete
                            </button>
                          </li>
                        </>
                      )}
                      
                      {!isOwner && (
                        <li>
                          <button
                            onClick={handleReport}
                            className="flex items-center w-full px-4 py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Flag size={16} className="mr-2" />
                            Report
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-2 line-clamp-2">
            <MarkdownRenderer content={question.content} />
          </div>
          
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <User size={16} className="mr-1" />
                <Link 
                  to={`/users/${question.userId}`}
                  className="hover:text-red-600 dark:hover:text-red-400"
                >
                  {question.userName}
                </Link>
              </span>
              <span className="flex items-center">
                <Calendar size={16} className="mr-1" />
                {formatDate(question.createdAt)}
              </span>
            </div>
            
            <Link
              to={`/courses/${courseId}/questions/${question.id}`}
              className="flex items-center mt-2 sm:mt-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            >
              <MessageSquare size={16} className="mr-1" />
              {question.answerCount || 0} {question.answerCount === 1 ? 'answer' : 'answers'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionItem;